import React, { FC, Fragment, useEffect, useState } from 'react';
import { useApolloClient, useLazyQuery, useReactiveVar } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import NotificationList from 'components/notifications/notification-list.component';
import { notificationsTemplate } from 'apollo/templates/notification';
import PageTitle from 'components/shared/page-title/page-title.component';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Tab,
    Tabs,
} from '@mui/material';
import {
    getClinicNotificationTypes,
    getExternalTechnicianNotificationTypes,
    getLabNotificationTypes,
    NotificationCategory,
    NotificationType,
} from 'models/notification';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { permissions } from 'utils/permission';

const Notifications: FC = () => {
    const [lastOffset, setLastOffset] = useState(0);
    const { t: translate } = useTranslation(['common', 'notifications']);
    const { showBoundary } = useDenthubErrorBoundary();
    const apolloClient = useApolloClient();
    const currentUser = useReactiveVar(currentUserVar);
    const currentOrganization = useReactiveVar(currentOrgVar);

    // The customer/supplier tabs and lab-configured default filters are for labs only
    const isLabUser = permissions.isLabTechnician(currentUser?.type as string);

    // Offer only the notification types from this audience's notification
    // settings: the lab's settings for lab users, the user's own account
    // settings for clinic users
    const getNotificationTypesForUser = () => {
        if (permissions.isExternalTechnician(currentUser?.role)) {
            return getExternalTechnicianNotificationTypes(currentOrganization?.externalGeneralNotifications);
        }
        if (permissions.isLabTechnician(currentUser?.type as string)) {
            return getLabNotificationTypes(currentOrganization?.generalNotifications);
        }
        if (permissions.isDentist(currentUser?.type as string)) {
            return getClinicNotificationTypes(currentUser?.generalNotifications);
        }
        return Object.values(NotificationType);
    };
    const notificationTypes = getNotificationTypesForUser();

    // Labs configure the default filters in lab settings (loaded with the user at app start)
    const defaultFilters = isLabUser ? currentOrganization?.notificationListDefaults : undefined;
    const [showOnlyUnread, setShowOnlyUnread] = useState(defaultFilters?.showOnlyUnread ?? true);
    const [showNotDone, setShowNotDone] = useState(defaultFilters?.showNotDone ?? true);
    const [selectedTypes, setSelectedTypes] = useState<NotificationType[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<NotificationCategory>(NotificationCategory.CUSTOMER);
    const [notifications, setNotifications] = useState([]);

    const [fetchNotifications, { fetchMore }] = useLazyQuery(notificationsTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-first',
        pollInterval: 1000 * 60,
        onCompleted: ({ notifications }) => {
            setLastOffset(notifications.length);
            setNotifications(notifications);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const fetchFirstPage = () => {
        fetchNotifications({
            variables: {
                limit: 20,
                offset: 0,
                onlyUnread: showOnlyUnread,
                onlyNotDone: showNotDone || undefined,
                types: selectedTypes.length ? selectedTypes : undefined,
                category: isLabUser ? selectedCategory : undefined,
            },
        });
        setLastOffset(0);
    };

    useEffect(() => {
        fetchFirstPage();
    }, [showOnlyUnread, showNotDone, selectedTypes, selectedCategory, isLabUser]);

    const handleSelectedTypesChange = (event: SelectChangeEvent<NotificationType[]>) => {
        const { value } = event.target;
        setSelectedTypes(typeof value === 'string' ? (value.split(',') as NotificationType[]) : value);
    };

    const handleCategoryChange = (category: NotificationCategory) => {
        setNotifications([]);
        setSelectedCategory(category);
    };

    const handleNotificationDoneChanged = () => {
        // Only when done notifications are hidden does a done change alter list
        // membership; drop the cached pages and refetch so the filter re-applies
        if (!showNotDone) {
            return;
        }
        apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'notifications' });
        apolloClient.cache.gc();
        fetchFirstPage();
    };

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
            });
        }
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('notifications:NOTIFICATIONS')} />
            <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Box>
                    <FormControl size="small" sx={{ minWidth: 240, maxWidth: 320 }}>
                        <InputLabel id="notification-type-filter-label">
                            {translate('notifications:FILTER_BY_TYPE')}
                        </InputLabel>
                        <Select
                            labelId="notification-type-filter-label"
                            label={translate('notifications:FILTER_BY_TYPE')}
                            multiple
                            value={selectedTypes}
                            onChange={handleSelectedTypesChange}
                            renderValue={(selected) =>
                                selected
                                    .map((notificationType) =>
                                        translate(`notifications:NOTIFICATION_${notificationType}`),
                                    )
                                    .join(', ')
                            }
                        >
                            {notificationTypes.map((notificationType) => (
                                <MenuItem key={notificationType} value={notificationType}>
                                    <Checkbox checked={selectedTypes.includes(notificationType)} />
                                    <ListItemText
                                        primary={translate(`notifications:NOTIFICATION_${notificationType}`)}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                {isLabUser && (
                    <Box>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id="notification-done-filter-label">
                                {translate('notifications:DONE')}
                            </InputLabel>
                            <Select
                                labelId="notification-done-filter-label"
                                label={translate('notifications:DONE')}
                                value={showNotDone ? 'notDone' : 'all'}
                                onChange={(event) => setShowNotDone(event.target.value === 'notDone')}
                            >
                                <MenuItem value="notDone">{translate('notifications:SHOW_NOT_DONE')}</MenuItem>
                                <MenuItem value="all">{translate('notifications:SHOW_ALL')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                )}
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox checked={showOnlyUnread} onChange={(_, checked) => setShowOnlyUnread(checked)} />
                        }
                        label={translate('notifications:SHOW_ONLY_UNREAD')}
                    />
                </Box>
            </Box>
            {isLabUser && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs value={selectedCategory} onChange={(_, category) => handleCategoryChange(category)}>
                        <Tab
                            label={translate('notifications:CUSTOMER_NOTIFICATIONS')}
                            value={NotificationCategory.CUSTOMER}
                            data-test="customer-notifications-tab"
                        />
                        <Tab
                            label={translate('notifications:SUPPLIER_NOTIFICATIONS')}
                            value={NotificationCategory.SUPPLIER}
                            data-test="supplier-notifications-tab"
                        />
                        {/* DD-3697: price requests and other events that are not about an order. */}
                        <Tab
                            label={translate('notifications:SYSTEM_NOTIFICATIONS')}
                            value={NotificationCategory.SYSTEM}
                            data-test="system-notifications-tab"
                        />
                    </Tabs>
                </Box>
            )}
            <NotificationList
                notifications={notifications}
                onFetchMore={handleFetchMore}
                onNotificationDoneChanged={handleNotificationDoneChanged}
            />
        </Fragment>
    );
};

export default Notifications;
